/**
 * Adds every member of a PDP bundle to the cart in one request.
 *
 * /cart/add.js takes an `items` array, so a bundle is a single POST rather than
 * one per member -- which matters because the cart quantity is what earns the
 * automatic multi-buy discount, and adding members one at a time would make the
 * drawer redraw at each intermediate discount tier.
 *
 * Two cases the markup cannot cover on its own:
 *   - a member already in the cart. Shopify increments it, which is correct:
 *     the customer ends up with both, and the higher quantity only helps them
 *     up the discount ladder.
 *   - a member that sells out between page render and click. Shopify rejects
 *     the WHOLE request, so nothing is added; we surface the message rather
 *     than leaving a spinner running.
 */
class GeBundleAdd extends HTMLElement {
  constructor() {
    super();
    this.button = this.querySelector('button');
    this.button?.addEventListener('click', this.onClick.bind(this));
  }

  get items() {
    return (this.dataset.ids || '')
      .split(',')
      .map((id) => id.trim())
      .filter(Boolean)
      .map((id) => ({ id: Number(id), quantity: 1 }));
  }

  onClick(event) {
    event.preventDefault();
    if (this.button.getAttribute('aria-disabled') === 'true') return;

    const items = this.items;
    if (!items.length) return;

    this.setLoading(true);

    // Ask for the same sections Dawn's own cart JS refreshes, so the drawer and
    // the header count both reflect the new cart without a page load.
    const drawer = document.querySelector('cart-drawer');
    const body = { items: items };
    if (drawer) {
      body.sections = ['cart-drawer', 'cart-icon-bubble'];
      body.sections_url = window.location.pathname;
    }

    fetch(`${window.routes.cart_add_url}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
      body: JSON.stringify(body),
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.status) {
          // Shopify returns `status` only on failure, and adds nothing at all.
          this.showError(data.description || data.message);
          return;
        }
        if (drawer && data.sections) {
          // The two steps Dawn's product-form.js takes around the same call.
          // Without them a bundle added to an EMPTY cart opened a blank
          // drawer: <cart-drawer> kept its is-empty class, whose CSS hides
          // #CartDrawer-Form and the header, and open() then looked for
          // .drawer__inner-empty (gone after the re-render) and called
          // trapFocus(null), which threw and left focus behind the modal.
          drawer.setActiveElement(this.button);
          drawer.classList.remove('is-empty');
          drawer.renderContents(data);
        } else {
          window.location = window.routes.cart_url;
        }
        this.clearError();
      })
      .catch(() => {
        this.showError('That did not go through. Please try again.');
      })
      .finally(() => {
        this.setLoading(false);
      });
  }

  setLoading(loading) {
    this.button.classList.toggle('loading', loading);
    this.button.setAttribute('aria-disabled', loading ? 'true' : 'false');
    this.querySelector('.loading__spinner')?.classList.toggle('hidden', !loading);
  }

  showError(message) {
    let el = this.querySelector('.ge-bundle__error');
    if (!el) {
      el = document.createElement('p');
      el.className = 'ge-bundle__error';
      el.setAttribute('role', 'alert');
      this.appendChild(el);
    }
    el.textContent = message;
  }

  clearError() {
    this.querySelector('.ge-bundle__error')?.remove();
  }
}

if (!customElements.get('ge-bundle-add')) {
  customElements.define('ge-bundle-add', GeBundleAdd);
}
